// Lo que escribe el asistente (D49), en bloques para dibujarlo: parrafos, titulos, listas, tablas Markdown, bloques de
// codigo (texto de ancho fijo: sus graficos de barras ███ quedan alineados) y bloques ```archivo nombre.csv``` que la
// pantalla convierte en un archivo para descargar. Funciones puras.

export type BloqueIA =
  | { tipo: 'parrafo'; texto: string }
  | { tipo: 'titulo'; texto: string }
  | { tipo: 'lista'; items: string[] }
  | { tipo: 'tabla'; encabezado: string[]; filas: string[][] }
  | { tipo: 'codigo'; texto: string }
  | { tipo: 'archivo'; nombre: string; contenido: string }
  // Un adjunto de un caso que la persona puede bajar (D57): ```adjunto nombre.pdf``` con "ref|casoId" dentro.
  | { tipo: 'adjunto'; nombre: string; ref: AdjuntoRef };

export type AdjuntoRef = { origen: 'zammad'; casoId: string; articulo: number; id: number } | { origen: 'correo'; mensajeId: string; parte: string };

// "z:<articulo>:<id>|<casoId>" o "c:<mensajeId>:<parte>|<casoId>". null si no tiene esa forma (no se inventa nada).
export function leerAdjuntoRef(t: string): AdjuntoRef | null {
  const [ref = '', casoId = ''] = t.trim().split('|').map((x) => x.trim());
  const z = /^z:(\d+):(\d+)$/.exec(ref);
  if (z && /^[0-9a-f-]{36}$/i.test(casoId)) return { origen: 'zammad', casoId, articulo: Number(z[1]), id: Number(z[2]) };
  const c = /^c:([0-9a-f-]{36}):([\d.]+)$/i.exec(ref);
  if (c) return { origen: 'correo', mensajeId: c[1], parte: c[2] };
  return null;
}

const celdas = (l: string) =>
  l
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split(/(?<!\\)\|/)
    .map((c) => c.trim().replace(/\\\|/g, '|'));
const esSeparador = (l: string) => /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/.test(l);
const esFila = (l: string) => /^\s*\|.*\|\s*$/.test(l);
const EXT_ARCHIVO = /\.(csv|md|txt|json|tsv|pdf|docx)$/i;

export function bloquesIA(texto: string): BloqueIA[] {
  const ls = texto.replace(/\r\n/g, '\n').split('\n');
  const out: BloqueIA[] = [];
  let lista: string[] = [];
  const cerrarLista = () => {
    if (lista.length) out.push({ tipo: 'lista', items: lista });
    lista = [];
  };
  for (let i = 0; i < ls.length; i++) {
    const l = ls[i];
    // Bloque ``` ... ```: archivo para descargar o texto de ancho fijo.
    const valla = /^\s*```\s*(.*)$/.exec(l);
    if (valla) {
      cerrarLista();
      const dentro: string[] = [];
      let j = i + 1;
      while (j < ls.length && !/^\s*```\s*$/.test(ls[j])) dentro.push(ls[j++]);
      i = j;
      const info = valla[1].trim();
      const adj = /^adjunto\s+(.+)$/i.exec(info)?.[1];
      const ref = adj ? leerAdjuntoRef(dentro.join(' ')) : null;
      if (adj && ref) {
        out.push({ tipo: 'adjunto', nombre: adj.trim(), ref });
        continue;
      }
      const archivo = adj ? null : (/^(?:archivo|file)\s+(.+)$/i.exec(info)?.[1] ?? (EXT_ARCHIVO.test(info) ? info : null));
      if (archivo) out.push({ tipo: 'archivo', nombre: nombreSeguro(archivo), contenido: dentro.join('\n') });
      else out.push({ tipo: 'codigo', texto: dentro.join('\n') });
      continue;
    }
    // Tabla: fila | a | b |, separador |---|, filas.
    if (esFila(l) && i + 1 < ls.length && esSeparador(ls[i + 1])) {
      cerrarLista();
      const encabezado = celdas(l);
      const filas: string[][] = [];
      let j = i + 2;
      while (j < ls.length && esFila(ls[j])) filas.push(celdas(ls[j++]));
      i = j - 1;
      out.push({ tipo: 'tabla', encabezado, filas });
      continue;
    }
    const item = /^\s*(?:[-*•]|\d+[.)])\s+(.*)$/.exec(l);
    if (item) {
      lista.push(item[1]);
      continue;
    }
    cerrarLista();
    if (!l.trim()) continue;
    const titulo = /^#{1,4}\s+(.*)$/.exec(l);
    out.push(titulo ? { tipo: 'titulo', texto: titulo[1] } : { tipo: 'parrafo', texto: l });
  }
  cerrarLista();
  return out;
}

// Nombre de archivo sin rutas ni caracteres raros; con extension conocida (si no, .txt).
export function nombreSeguro(n: string) {
  const base = n.split(/[\\/]/).pop()!.replace(/[^\p{L}\p{N}._-]+/gu, '-').replace(/^-+|-+$/g, '').slice(0, 80) || 'archivo';
  return EXT_ARCHIVO.test(base) ? base : `${base}.txt`;
}

// Tabla a CSV (comillas cuando hace falta). Se quita el **negrita** de Markdown.
export function tablaACsv(encabezado: string[], filas: string[][]) {
  const campo = (v: string) => {
    const t = v.replace(/\*\*(.+?)\*\*/g, '$1');
    return /[",\n;]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t;
  };
  return [encabezado, ...filas].map((f) => f.map(campo).join(',')).join('\r\n');
}

export const tipoArchivo = (nombre: string) =>
  /\.csv$/i.test(nombre) ? 'text/csv' : /\.json$/i.test(nombre) ? 'application/json' : /\.md$/i.test(nombre) ? 'text/markdown' : 'text/plain';

// Filas de un CSV (para mostrar cuantas trae). Cuenta saltos fuera de comillas.
export function filasCsv(contenido: string) {
  let n = 0;
  let enComillas = false;
  const t = contenido.trim();
  if (!t) return 0;
  for (const ch of t) {
    if (ch === '"') enComillas = !enComillas;
    else if (ch === '\n' && !enComillas) n++;
  }
  return n; // sin contar el encabezado
}

// Descarga un texto como archivo. CSV con BOM: Excel respeta las tildes.
export function descargarTexto(nombre: string, contenido: string) {
  const tipo = tipoArchivo(nombre);
  const blob = new Blob([tipo === 'text/csv' ? '﻿' : '', contenido], { type: `${tipo};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/* ---------------- Markdown a HTML para el correo (D98) ---------------- */

const esc = (t: string) => t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
// Negrita, cursiva, `codigo` y [texto](url) (solo http(s), mailto y tel) sobre texto ya escapado.
const enLineaHtml = (t: string) =>
  esc(t)
    .replace(/`([^`\n]+)`/g, '<code style="font-family:Consolas,monospace;background:#f1f4f5;padding:0 3px;border-radius:3px">$1</code>')
    .replace(/\*\*([^*\n]+)\*\*/g, '<b>$1</b>')
    .replace(/(^|[\s(])\*([^*\s][^*\n]*?)\*(?=[\s).,;:!?]|$)/g, '$1<i>$2</i>')
    .replace(/\[([^\]\n]+)\]\(((?:https?:\/\/|mailto:|tel:)[^\s)]+)\)/g, '<a href="$2">$1</a>');

const TD = 'border:1px solid #c9d3d6;padding:6px 10px;text-align:left;vertical-align:top';
const TH = `${TD};background:#1f4e5f;color:#ffffff;font-weight:bold`;

/**
 * El texto de una respuesta (escrito a mano o por AMIX) en HTML para el correo: parrafos (las lineas seguidas van en
 * el mismo parrafo), titulos #, listas con viñetas o numeros, tablas | a | b | con bordes, ``` de ancho fijo, negrita,
 * cursiva y enlaces. Los estilos van en linea porque Outlook y Gmail ignoran las hojas de estilo.
 */
export function markdownAHtml(texto: string) {
  const ls = texto.replace(/\r\n/g, '\n').trim().split('\n');
  const out: string[] = [];
  let parrafo: string[] = [];
  const cerrarParrafo = () => {
    if (parrafo.length) out.push(`<p style="margin:0 0 12px">${parrafo.map(enLineaHtml).join('<br>')}</p>`);
    parrafo = [];
  };
  for (let i = 0; i < ls.length; i++) {
    const l = ls[i];
    if (!l.trim()) {
      cerrarParrafo();
      continue;
    }
    if (/^\s*```/.test(l)) {
      cerrarParrafo();
      const dentro: string[] = [];
      let j = i + 1;
      while (j < ls.length && !/^\s*```\s*$/.test(ls[j])) dentro.push(ls[j++]);
      i = j;
      out.push(`<pre style="font-family:Consolas,monospace;font-size:13px;background:#f1f4f5;padding:10px;border-radius:6px;white-space:pre-wrap">${esc(dentro.join('\n'))}</pre>`);
      continue;
    }
    if (esFila(l) && i + 1 < ls.length && esSeparador(ls[i + 1])) {
      cerrarParrafo();
      const enc = celdas(l);
      const filas: string[][] = [];
      let j = i + 2;
      while (j < ls.length && esFila(ls[j])) filas.push(celdas(ls[j++]));
      i = j - 1;
      out.push(
        `<table style="border-collapse:collapse;margin:0 0 14px;font-size:14px"><thead><tr>${enc.map((c) => `<th style="${TH}">${enLineaHtml(c)}</th>`).join('')}</tr></thead><tbody>${filas
          .map((f) => `<tr>${f.map((c) => `<td style="${TD}">${enLineaHtml(c)}</td>`).join('')}</tr>`)
          .join('')}</tbody></table>`,
      );
      continue;
    }
    const titulo = /^\s*#{1,4}\s+(.*)$/.exec(l);
    if (titulo) {
      cerrarParrafo();
      out.push(`<p style="margin:16px 0 8px;font-weight:bold;font-size:15px">${enLineaHtml(titulo[1].replace(/^\*\*(.*)\*\*$/, '$1'))}</p>`);
      continue;
    }
    const vineta = /^\s*[-*•]\s+(.*)$/.exec(l);
    const numero = /^\s*\d+[.)]\s+(.*)$/.exec(l);
    if (vineta || numero) {
      cerrarParrafo();
      const tag = vineta ? 'ul' : 'ol';
      const items: string[] = [];
      let j = i;
      for (; j < ls.length; j++) {
        const m = vineta ? /^\s*[-*•]\s+(.*)$/.exec(ls[j]) : /^\s*\d+[.)]\s+(.*)$/.exec(ls[j]);
        if (!m) break;
        items.push(m[1]);
      }
      i = j - 1;
      out.push(`<${tag} style="margin:0 0 12px;padding-left:24px">${items.map((x) => `<li style="margin:0 0 4px">${enLineaHtml(x)}</li>`).join('')}</${tag}>`);
      continue;
    }
    parrafo.push(l);
  }
  cerrarParrafo();
  return out.join('');
}
